/**
 * useCombat Hook
 * Phase 6-3: 攻撃処理（executeAttack, attack）
 *
 * executeAttack: 実際の攻撃処理（ダメージ計算、破壊処理）
 * attack: チェーン確認を挟む公開API
 */

import React, { useCallback } from 'react';
import {
  COUNTER_ATTACK_RATE,
  PIERCING_DAMAGE_RATE,
  LONE_WARRIOR_BONUS,
  RULE_LONE_WARRIOR,
} from '../../utils/constants';
import {
  fireTrigger,
  fireLeaveFieldTrigger,
  unregisterCardTriggers,
  registerCardTriggers,
} from '../../engine/triggerEngine';
import { TRIGGER_TYPES } from '../../engine/triggerTypes';
import { continuousEffectEngine } from '../../engine/continuousEffects';
import { statusEffectEngine } from '../../engine/statusEffects';
import {
  CHAIN_POINTS,
  handleLinkBreak,
  shouldApplyShishoku,
  shouldApplyDokushin,
  canIgnoreBlockWithIatsuko,
  hasHikou,
  isGroundMonster,
  getValidAttackTargets,
  getDirectAttackStatus,
  getCounterAttackMultiplier,
  calculateFlightCounterDamage,
} from '../../engine/keywordAbilities';

import type { MonsterInstance, CardData, HandCard, FieldCardInstance } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type { ChainConfirmation, PendingSetsunaAction } from '../../types/ui';
import type { PlayerStatusEffect } from '../../engine/statusEffects/statusEngine';

/** useCombat 依存オブジェクト */
interface UseCombatDeps {
  // State
  currentPlayer: PlayerNumber;
  isFirstTurn: boolean;
  firstPlayer: PlayerNumber;
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  p1Hand: HandCard[];
  p2Hand: HandCard[];
  p1Deck: CardData[];
  p2Deck: CardData[];
  p1Graveyard: CardData[];
  p2Graveyard: CardData[];
  p1FieldCard: FieldCardInstance | null;
  p2FieldCard: FieldCardInstance | null;
  p1Life: number;
  p2Life: number;
  p1StatusEffects: PlayerStatusEffect[];
  p2StatusEffects: PlayerStatusEffect[];
  loneWarriorRule: boolean;
  piercingDamage: boolean;
  chainConfirmation: ChainConfirmation | null;

  // Setters
  setP1Life: React.Dispatch<React.SetStateAction<number>>;
  setP2Life: React.Dispatch<React.SetStateAction<number>>;
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP1Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP2Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP1Deck: React.Dispatch<React.SetStateAction<CardData[]>>;
  setP2Deck: React.Dispatch<React.SetStateAction<CardData[]>>;
  setP1Graveyard: React.Dispatch<React.SetStateAction<CardData[]>>;
  setP2Graveyard: React.Dispatch<React.SetStateAction<CardData[]>>;
  setP1FieldCard: React.Dispatch<React.SetStateAction<FieldCardInstance | null>>;
  setP2FieldCard: React.Dispatch<React.SetStateAction<FieldCardInstance | null>>;
  setP1StatusEffects: React.Dispatch<React.SetStateAction<PlayerStatusEffect[]>>;
  setP2StatusEffects: React.Dispatch<React.SetStateAction<PlayerStatusEffect[]>>;
  setAttackingMonster: React.Dispatch<React.SetStateAction<number | null>>;
  setSelectedFieldMonster: React.Dispatch<React.SetStateAction<number | null>>;

  // Functions
  startChainConfirmation: (chainPoint: string, pendingAction: PendingSetsunaAction, context?: Record<string, unknown>) => boolean;
  addLog: (message: string, type?: string) => void;
}

/** useCombat 戻り値 */
interface UseCombatReturn {
  executeAttack: (attackerIndex: number, targetIndex: number | null) => void;
  attack: (attackerIndex: number, targetIndex: number | null) => void;
}

/**
 * useCombat Hook
 * @param deps - 依存オブジェクト
 */
export const useCombat = (deps: UseCombatDeps): UseCombatReturn => {
  const {
    // State
    currentPlayer,
    isFirstTurn,
    firstPlayer,
    p1Field,
    p2Field,
    p1Hand,
    p2Hand,
    p1Deck,
    p2Deck,
    p1Graveyard,
    p2Graveyard,
    p1FieldCard,
    p2FieldCard,
    p1Life,
    p2Life,
    p1StatusEffects,
    p2StatusEffects,
    loneWarriorRule,
    piercingDamage,
    chainConfirmation,

    // Setters
    setP1Life,
    setP2Life,
    setP1Field,
    setP2Field,
    setP1Hand,
    setP2Hand,
    setP1Deck,
    setP2Deck,
    setP1Graveyard,
    setP2Graveyard,
    setP1FieldCard,
    setP2FieldCard,
    setP1StatusEffects,
    setP2StatusEffects,
    setAttackingMonster,
    setSelectedFieldMonster,

    // Functions
    startChainConfirmation,
    addLog,
  } = deps;

  // =============================================================================
  // 攻撃実行（内部関数）
  // =============================================================================

  const executeAttack = useCallback((attackerIndex: number, targetIndex: number | null): void => {
    // 先攻1ターン目は攻撃不可（二重チェック）
    if (isFirstTurn && currentPlayer === firstPlayer) {
      addLog('先攻1ターン目は攻撃できません', 'info');
      return;
    }

    // 現在のプレイヤーと相手のフィールドを直接取得
    const playerField = currentPlayer === 1 ? p1Field : p2Field;
    const opponentField = currentPlayer === 1 ? p2Field : p1Field;

    const attacker = playerField[attackerIndex];
    if (!attacker || !attacker.canAttack) {
      addLog('このモンスターは攻撃できません', 'damage');
      return;
    }

    // 常時効果による攻撃制限をチェック
    const effectContext = {
      currentPlayer,
      effectOwner: currentPlayer,
      p1Field,
      p2Field,
      p1Life,
      p2Life,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!continuousEffectEngine.canAttack(attacker as any, effectContext)) {
      addLog(`${attacker.name}は常時効果により攻撃できません！`, 'damage');
      return;
    }

    // 状態異常による攻撃制限をチェック（眠り、凍結、行動不能）
    if (!statusEffectEngine.canAttack(attacker)) {
      const statusName = statusEffectEngine.getBlockingStatusName(attacker, 'attack');
      addLog(`${attacker.name}は${statusName}により攻撃できません！`, 'damage');
      return;
    }

    const target = targetIndex !== null ? opponentField[targetIndex] : null;

    // 常時効果による攻撃力修正を計算
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const attackerAtkMod = continuousEffectEngine.calculateAttackModifier(attacker as any, effectContext);
    // 状態異常による攻撃力修正を計算（凍結-50%、雷撃-500）
    const attackerStatusMod = statusEffectEngine.getAttackModifier(attacker);
    let effectiveAttackerAtk = Math.max(0, attacker.currentAttack + attackerAtkMod + attackerStatusMod);

    // 【孤軍奮闘】自分のモンスターが相手より少ない場合の補正
    let loneWarriorAtkBonus = 0;
    let loneWarriorDamageReduction = 0;
    if (loneWarriorRule) {
      const myMonsterCount = playerField.filter(m => m !== null).length;
      const opponentMonsterCount = opponentField.filter(m => m !== null).length;
      const countDiff = opponentMonsterCount - myMonsterCount;

      if (countDiff > 0) {
        const bonusLevel = Math.min(countDiff, 3); // 最大3体差
        const bonus = LONE_WARRIOR_BONUS[bonusLevel];
        loneWarriorAtkBonus = bonus.atkBonus;
        loneWarriorDamageReduction = bonus.damageReduction;

        const atkIncrease = Math.floor(effectiveAttackerAtk * loneWarriorAtkBonus);
        effectiveAttackerAtk += atkIncrease;
        addLog(`【孤軍奮闘】${countDiff}体差！ATK+${Math.floor(loneWarriorAtkBonus * 100)}%（+${atkIncrease}）`, 'info');
      }
    }

    if (target && targetIndex !== null) {
      // モンスター攻撃
      // 飛行/対空による攻撃対象制限チェック
      const validTargets = getValidAttackTargets(attacker, opponentField);
      if (!validTargets.includes(targetIndex)) {
        addLog(`${attacker.name}は${target.name}を攻撃できません（飛行モンスターには対空が必要）`, 'damage');
        setAttackingMonster(null);
        setSelectedFieldMonster(null);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const targetAtkMod = continuousEffectEngine.calculateAttackModifier(target as any, { ...effectContext, effectOwner: currentPlayer === 1 ? 2 : 1 });
      const targetStatusMod = statusEffectEngine.getAttackModifier(target);
      const effectiveTargetAtk = Math.max(0, target.currentAttack + targetAtkMod + targetStatusMod);

      let damage = effectiveAttackerAtk;
      // 状態異常による被ダメージ倍率（濡れ: 2倍）
      const damageMultiplier = statusEffectEngine.getDamageMultiplier(target);
      damage = Math.floor(damage * damageMultiplier);
      // 状態異常による軽減（守護: 50%）
      const { reduction, usedGuard, updatedMonster: targetAfterGuard } = statusEffectEngine.calculateDamageReduction(target, damage);
      damage = Math.max(0, damage - reduction);

      // 飛行/対空による反撃ダメージ倍率を計算
      const flightCounterRate = getCounterAttackMultiplier(attacker, target);
      let counterDamage = Math.floor(effectiveTargetAtk * flightCounterRate);

      // 飛行→地上の場合、反撃なし
      if (flightCounterRate === 0 && hasHikou(attacker) && isGroundMonster(target)) {
        addLog(`【飛行】${attacker.name}は地上モンスターからの反撃を受けない！`, 'info');
      }

      // 反撃無効フラグチェック（呪影の牙狼ウルフェイン等）
      if (attacker.noCounterDamage) {
        counterDamage = 0;
        addLog(`${attacker.name}は反撃ダメージを受けない！`, 'info');
      }

      // 反撃ダメージ率変更フラグチェック（棘の剛毛猪イノブリスト等）
      if (target.counterDamageRate !== undefined && target.counterDamageRate !== COUNTER_ATTACK_RATE) {
        counterDamage = Math.floor(effectiveTargetAtk * target.counterDamageRate);
        addLog(`${target.name}の反撃は${Math.floor(target.counterDamageRate * 100)}%！`, 'info');
      }

      // 【孤軍奮闘】被ダメージ軽減を反撃に適用
      if (RULE_LONE_WARRIOR && loneWarriorDamageReduction > 0) {
        const reduction = Math.floor(counterDamage * loneWarriorDamageReduction);
        counterDamage -= reduction;
        addLog(`【孤軍奮闘】被ダメ-${Math.floor(loneWarriorDamageReduction * 100)}%（-${reduction}）`, 'info');
      }

      addLog(`${attacker.name}が${target.name}を攻撃！`, 'info');

      // 攻撃トリガーを発火
      const attackTriggerContext = {
        currentPlayer,
        attacker,
        attackerIndex,
        target,
        targetIndex: targetIndex ?? undefined,
        damage: effectiveAttackerAtk,
        p1Field,
        p2Field,
        p1Life,
        p2Life,
        setP1Life,
        setP2Life,
        setP1Field,
        setP2Field,
        p1Hand,
        p2Hand,
        setP1Hand,
        setP2Hand,
        p1Deck,
        p2Deck,
        setP1Deck,
        setP2Deck,
        p1Graveyard,
        p2Graveyard,
        setP1Graveyard,
        setP2Graveyard,
        addLog,
        // 攻撃時トリガーで蓄積されるダメージボーナス（React state更新の非同期問題を回避）
        accumulatedDamageBonus: 0,
      };
      // 攻撃者のON_ATTACKトリガー
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fireTrigger(TRIGGER_TYPES.ON_ATTACK, attackTriggerContext as any);
      // 攻撃対象のON_ATTACKEDトリガー
      const attackedContext = {
        ...attackTriggerContext,
        currentPlayer: currentPlayer === 1 ? 2 : 1, // 被攻撃者視点
        card: target,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fireTrigger(TRIGGER_TYPES.ON_ATTACKED, attackedContext as any);
      // 相手のON_OPPONENT_ATTACKトリガー（相手視点で「自分のモンスターが攻撃された」）
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fireTrigger(TRIGGER_TYPES.ON_OPPONENT_ATTACK, attackedContext as any);

      // accumulatedDamageBonus をダメージに加算（イグニファトゥス等の攻撃時効果）
      if (attackTriggerContext.accumulatedDamageBonus > 0) {
        damage += attackTriggerContext.accumulatedDamageBonus;
      }

      // トリガーによるダメージボーナスをチェック（群狼長ヴァルグレイス等）
      // トリガーでフィールドが更新されている可能性があるので再取得
      const updatedAttackerField = currentPlayer === 1 ? p1Field : p2Field;
      const updatedAttacker = updatedAttackerField[attackerIndex];
      if (updatedAttacker && updatedAttacker.attackDamageBonus) {
        damage += updatedAttacker.attackDamageBonus;
        // ボーナスをクリア（1回限り）
        const setAttackerField = currentPlayer === 1 ? setP1Field : setP2Field;
        setAttackerField(prev => {
          const newField = [...prev];
          if (newField[attackerIndex]) {
            const { attackDamageBonus, ...rest } = newField[attackerIndex];
            newField[attackerIndex] = rest;
          }
          return newField;
        });
      }

      // ダメージ処理（新しいオブジェクトを作成）
      // 守護を使用した場合、更新されたモンスターを使用
      const targetForDamage = (usedGuard ? targetAfterGuard : target) as MonsterInstance;
      let newTargetHp = (targetForDamage.currentHp ?? 0) - damage;
      let newAttackerHp = (attacker.currentHp ?? 0) - counterDamage;

      // 破壊耐性チェック: HP0以下になる場合はHP1で止まる
      const targetHasIndestructible = (targetForDamage as MonsterInstance).indestructibleUntilEndOfTurn;
      const attackerHasIndestructible = attacker.indestructibleUntilEndOfTurn;

      if (targetHasIndestructible && newTargetHp <= 0) {
        newTargetHp = 1;
      }
      if (attackerHasIndestructible && newAttackerHp <= 0) {
        newAttackerHp = 1;
      }

      if (damageMultiplier > 1) {
        addLog(`${target.name}は濡れ状態でダメージ増加！`, 'info');
      }
      if (usedGuard) {
        addLog(`${target.name}の守護でダメージ軽減！`, 'info');
      }
      addLog(`${target.name}に${damage}ダメージ！`, 'damage');

      // 【死触】チェック: ダメージが1以上であれば、HPに関係なく相手モンスターを破壊
      const shishokuTriggered = shouldApplyShishoku(attacker, damage);
      if (shishokuTriggered) {
        addLog(`【死触】${attacker.name}のダメージにより${target.name}は即座に破壊される！`, 'damage');
      }

      // 破壊耐性による救済（【死触】含む）
      const targetShouldBeDestroyed = newTargetHp <= 0 || shishokuTriggered;
      if (targetHasIndestructible && targetShouldBeDestroyed) {
        addLog(`${target.name}は破壊されない！（HP: 1）`, 'info');
      }
      addLog(`反撃で${attacker.name}に${counterDamage}ダメージ！`, 'damage');
      if (attackerHasIndestructible && attacker.currentHp - counterDamage <= 0) {
        addLog(`${attacker.name}は破壊されない！（HP: 1）`, 'info');
      }

      // 【貫通ダメージ】モンスター破壊時に余剰ダメージの50%を相手ライフに与える
      // 【死触】による破壊は貫通ダメージなし（余剰ダメージがないため）
      let piercingDmgValue = 0;
      const targetWillBeDestroyedByDamage = newTargetHp <= 0 && !targetHasIndestructible;
      if (piercingDamage && targetWillBeDestroyedByDamage) {
        const excessDamage = damage - targetForDamage.currentHp;
        piercingDmgValue = Math.floor(excessDamage * PIERCING_DAMAGE_RATE);
        if (piercingDmgValue > 0) {
          addLog(`【貫通ダメージ】余剰${excessDamage}の${Math.floor(PIERCING_DAMAGE_RATE * 100)}%→${piercingDmgValue}ダメージ！`, 'damage');
        }
      }

      // 相手フィールドの更新
      // 【死触】による破壊も含む（破壊耐性がない場合）
      const targetDestroyed = targetShouldBeDestroyed && !targetHasIndestructible;
      if (currentPlayer === 1) {
        // プレイヤー1が攻撃 → 相手はプレイヤー2
        if (targetDestroyed) {
          // 破壊時トリガーを発火（破壊される前）
          const destroyContext = {
            currentPlayer: 2, // 破壊されるモンスターのオーナー
            destroyedCard: target,
            destroyedSlotIndex: targetIndex,
            setP1Life,
            setP2Life,
            setP1Field,
            setP2Field,
            setP1Hand,
            setP2Hand,
            setP1Deck,
            setP2Deck,
            setP1Graveyard,
            setP2Graveyard,
            setP1FieldCard,
            setP2FieldCard,
            p1Field,
            p2Field,
            p1Hand,
            p2Hand,
            p1Deck,
            p2Deck,
            p1Graveyard,
            p2Graveyard,
            p1FieldCard,
            p2FieldCard,
            p1Life,
            p2Life,
            addLog,
            registerCardTriggers,
            continuousEffectEngine,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireTrigger(TRIGGER_TYPES.ON_DESTROY_SELF, destroyContext as any);
          // 場を離れる時トリガーを発火
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireLeaveFieldTrigger(target, destroyContext as any, 'destroy');

          // 【魂結】リンク解除処理
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handleLinkBreak(target, setP2Field as any, addLog);

          // トリガー登録を解除
          unregisterCardTriggers(target.uniqueId);
          // 常時効果を解除
          continuousEffectEngine.unregister(target.uniqueId);

          setP2Field(prev => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
          setP2Graveyard(prev => [...prev, target]);
          addLog(`${target.name}は破壊された！`, 'damage');

          // ON_ATTACK_SUCCESS トリガーを発火（攻撃でモンスター破壊時）
          const attackSuccessContext = {
            currentPlayer: 1, // 攻撃者のオーナー
            card: attacker,
            slotIndex: attackerIndex,
            destroyedCard: target,
            setP1Life,
            setP2Life,
            setP1Field,
            setP2Field,
            setP1Hand,
            setP2Hand,
            setP1Deck,
            setP2Deck,
            setP1Graveyard,
            setP2Graveyard,
            setP1FieldCard,
            setP2FieldCard,
            p1Field,
            p2Field,
            p1Hand,
            p2Hand,
            p1Deck,
            p2Deck,
            p1Graveyard,
            p2Graveyard,
            p1FieldCard,
            p2FieldCard,
            p1Life,
            p2Life,
            addLog,
            registerCardTriggers,
            continuousEffectEngine,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireTrigger(TRIGGER_TYPES.ON_ATTACK_SUCCESS, attackSuccessContext as any);

          // 【貫通ダメージ】相手ライフに適用
          if (piercingDmgValue > 0) {
            setP2Life(prev => Math.max(0, prev - piercingDmgValue));
          }
        } else {
          setP2Field(prev => {
            const newField = [...prev];
            // 守護使用後のモンスター状態を反映
            newField[targetIndex] = { ...targetForDamage, currentHp: newTargetHp };
            return newField;
          });
        }

        // 自分のフィールドの更新
        if (newAttackerHp <= 0) {
          // 破壊時トリガーを発火（破壊される前）
          const destroyContext = {
            currentPlayer: 1, // 破壊されるモンスターのオーナー
            destroyedCard: attacker,
            destroyedSlotIndex: attackerIndex,
            setP1Life,
            setP2Life,
            setP1Field,
            setP2Field,
            setP1Hand,
            setP2Hand,
            setP1Deck,
            setP2Deck,
            setP1Graveyard,
            setP2Graveyard,
            setP1FieldCard,
            setP2FieldCard,
            p1Field,
            p2Field,
            p1Hand,
            p2Hand,
            p1Deck,
            p2Deck,
            p1Graveyard,
            p2Graveyard,
            p1FieldCard,
            p2FieldCard,
            p1Life,
            p2Life,
            addLog,
            registerCardTriggers,
            continuousEffectEngine,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireTrigger(TRIGGER_TYPES.ON_DESTROY_SELF, destroyContext as any);
          // 場を離れる時トリガーを発火
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireLeaveFieldTrigger(attacker, destroyContext as any, 'destroy');

          // 【魂結】リンク解除処理
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handleLinkBreak(attacker, setP1Field as any, addLog);

          // トリガー登録を解除
          unregisterCardTriggers(attacker.uniqueId);
          // 常時効果を解除
          continuousEffectEngine.unregister(attacker.uniqueId);

          setP1Field(prev => {
            const newField = [...prev];
            newField[attackerIndex] = null;
            return newField;
          });
          setP1Graveyard(prev => [...prev, attacker]);
          addLog(`${attacker.name}は破壊された！`, 'damage');
        } else {
          setP1Field(prev => {
            const newField = [...prev];
            // prevから最新のモンスターを取得（トリガーによる変更を保持）
            const currentAttacker = newField[attackerIndex];
            if (currentAttacker) {
              newField[attackerIndex] = { ...currentAttacker, currentHp: newAttackerHp, canAttack: false };
            }
            return newField;
          });
        }
      } else {
        // プレイヤー2が攻撃 → 相手はプレイヤー1
        if (targetDestroyed) {
          // 破壊時トリガーを発火（破壊される前）
          const destroyContext = {
            currentPlayer: 1, // 破壊されるモンスターのオーナー
            destroyedCard: target,
            destroyedSlotIndex: targetIndex,
            setP1Life,
            setP2Life,
            setP1Field,
            setP2Field,
            setP1Hand,
            setP2Hand,
            setP1Deck,
            setP2Deck,
            setP1Graveyard,
            setP2Graveyard,
            setP1FieldCard,
            setP2FieldCard,
            p1Field,
            p2Field,
            p1Hand,
            p2Hand,
            p1Deck,
            p2Deck,
            p1Graveyard,
            p2Graveyard,
            p1FieldCard,
            p2FieldCard,
            p1Life,
            p2Life,
            addLog,
            registerCardTriggers,
            continuousEffectEngine,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireTrigger(TRIGGER_TYPES.ON_DESTROY_SELF, destroyContext as any);
          // 場を離れる時トリガーを発火
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireLeaveFieldTrigger(target, destroyContext as any, 'destroy');

          // 【魂結】リンク解除処理
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handleLinkBreak(target, setP1Field as any, addLog);

          // トリガー登録を解除
          unregisterCardTriggers(target.uniqueId);
          // 常時効果を解除
          continuousEffectEngine.unregister(target.uniqueId);

          setP1Field(prev => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
          setP1Graveyard(prev => [...prev, target]);
          addLog(`${target.name}は破壊された！`, 'damage');

          // ON_ATTACK_SUCCESS トリガーを発火（攻撃でモンスター破壊時）
          const attackSuccessContext = {
            currentPlayer: 2, // 攻撃者のオーナー
            card: attacker,
            slotIndex: attackerIndex,
            destroyedCard: target,
            setP1Life,
            setP2Life,
            setP1Field,
            setP2Field,
            setP1Hand,
            setP2Hand,
            setP1Deck,
            setP2Deck,
            setP1Graveyard,
            setP2Graveyard,
            setP1FieldCard,
            setP2FieldCard,
            p1Field,
            p2Field,
            p1Hand,
            p2Hand,
            p1Deck,
            p2Deck,
            p1Graveyard,
            p2Graveyard,
            p1FieldCard,
            p2FieldCard,
            p1Life,
            p2Life,
            addLog,
            registerCardTriggers,
            continuousEffectEngine,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireTrigger(TRIGGER_TYPES.ON_ATTACK_SUCCESS, attackSuccessContext as any);

          // 【貫通ダメージ】相手ライフに適用
          if (piercingDmgValue > 0) {
            setP1Life(prev => Math.max(0, prev - piercingDmgValue));
          }
        } else {
          setP1Field(prev => {
            const newField = [...prev];
            // 守護使用後のモンスター状態を反映
            newField[targetIndex] = { ...targetForDamage, currentHp: newTargetHp };
            return newField;
          });
        }

        // 自分のフィールドの更新
        if (newAttackerHp <= 0) {
          // 破壊時トリガーを発火（破壊される前）
          const destroyContext = {
            currentPlayer: 2, // 破壊されるモンスターのオーナー
            destroyedCard: attacker,
            destroyedSlotIndex: attackerIndex,
            setP1Life,
            setP2Life,
            setP1Field,
            setP2Field,
            setP1Hand,
            setP2Hand,
            setP1Deck,
            setP2Deck,
            setP1Graveyard,
            setP2Graveyard,
            setP1FieldCard,
            setP2FieldCard,
            p1Field,
            p2Field,
            p1Hand,
            p2Hand,
            p1Deck,
            p2Deck,
            p1Graveyard,
            p2Graveyard,
            p1FieldCard,
            p2FieldCard,
            p1Life,
            p2Life,
            addLog,
            registerCardTriggers,
            continuousEffectEngine,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireTrigger(TRIGGER_TYPES.ON_DESTROY_SELF, destroyContext as any);
          // 場を離れる時トリガーを発火
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireLeaveFieldTrigger(attacker, destroyContext as any, 'destroy');

          // 【魂結】リンク解除処理
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handleLinkBreak(attacker, setP2Field as any, addLog);

          // トリガー登録を解除
          unregisterCardTriggers(attacker.uniqueId);
          // 常時効果を解除
          continuousEffectEngine.unregister(attacker.uniqueId);

          setP2Field(prev => {
            const newField = [...prev];
            newField[attackerIndex] = null;
            return newField;
          });
          setP2Graveyard(prev => [...prev, attacker]);
          addLog(`${attacker.name}は破壊された！`, 'damage');
        } else {
          setP2Field(prev => {
            const newField = [...prev];
            // prevから最新のモンスターを取得（トリガーによる変更を保持）
            const currentAttacker = newField[attackerIndex];
            if (currentAttacker) {
              newField[attackerIndex] = { ...currentAttacker, currentHp: newAttackerHp, canAttack: false };
            }
            return newField;
          });
        }
      }
    } else {
      // ダイレクトアタック判定
      // プレイヤーステータスによるダイレクトアタック禁止をチェック
      const myStatusEffects = currentPlayer === 1 ? p1StatusEffects : p2StatusEffects;
      if (!statusEffectEngine.canDirectAttack(myStatusEffects)) {
        addLog('このターン中はダイレクトアタックできません！', 'damage');
        setAttackingMonster(null);
        setSelectedFieldMonster(null);
        return;
      }

      // 飛行/対空システムによるダイレクトアタック可否を判定
      const directStatus = getDirectAttackStatus(attacker, opponentField);

      // 【威圧吼】チェック: 相手モンスター1体以下ならブロック無視
      const iatsukoIgnoresBlock = canIgnoreBlockWithIatsuko(attacker, opponentField);

      // ダイレクトアタック可否判定
      // 威圧吼でブロック無視の場合は強制的に可能
      // それ以外は飛行システムの判定に従う
      if (!directStatus.canDirect && !iatsukoIgnoresBlock) {
        addLog(directStatus.reason, 'damage');
        setAttackingMonster(null);
        setSelectedFieldMonster(null);
        return;
      }

      let damage = effectiveAttackerAtk; // 常時効果による修正を適用
      const opponentFieldCard = currentPlayer === 1 ? p2FieldCard : p1FieldCard;

      // ダメージ軽減の適用
      if (iatsukoIgnoresBlock) {
        // 威圧吼でブロック無視 → 軽減なし
        addLog(`【威圧吼】${attacker.name}は相手モンスター1体のブロックを無視！`, 'info');
      } else if (directStatus.damageMultiplier < 1) {
        // 飛行システムによる軽減（地上を無視 or 飛行のみでダイレクト）
        damage = Math.floor(damage * directStatus.damageMultiplier);
        addLog(directStatus.reason, 'info');
      }

      if (opponentFieldCard) {
        damage = Math.floor(damage * 0.75);
        addLog(`フィールドカードによりダメージ75%`, 'info');
      }

      addLog(`${attacker.name}がダイレクトアタック！${damage}ダメージ！`, 'damage');

      // 地上→飛行のみの場合、飛行モンスターからの反撃ダメージを計算
      const flightCounterDamage = calculateFlightCounterDamage(opponentField, directStatus.counterMultiplier);
      let attackerDamaged = false;
      let newAttackerHpAfterCounter = attacker.currentHp ?? 0;
      if (flightCounterDamage > 0) {
        addLog(`【飛行】飛行モンスターからの反撃で${attacker.name}に${flightCounterDamage}ダメージ！`, 'damage');
        newAttackerHpAfterCounter = (attacker.currentHp ?? 0) - flightCounterDamage;
        attackerDamaged = true;
      }

      // ダイレクトアタック時の攻撃トリガーを発火
      const directAttackTriggerContext = {
        currentPlayer,
        attacker,
        attackerIndex,
        target: null, // ダイレクトアタックなのでターゲットなし
        targetIndex: -1,
        damage,
        isDirect: true,
        p1Field,
        p2Field,
        p1Life,
        p2Life,
        setP1Life,
        setP2Life,
        setP1Field,
        setP2Field,
        p1Hand,
        p2Hand,
        setP1Hand,
        setP2Hand,
        p1Deck,
        p2Deck,
        setP1Deck,
        setP2Deck,
        p1Graveyard,
        p2Graveyard,
        setP1Graveyard,
        setP2Graveyard,
        addLog,
        // 攻撃時トリガーで蓄積されるダメージボーナス（React state更新の非同期問題を回避）
        accumulatedDamageBonus: 0,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fireTrigger(TRIGGER_TYPES.ON_ATTACK, directAttackTriggerContext as any);

      // accumulatedDamageBonus をダメージに加算（イグニファトゥス等の攻撃時効果）
      let finalDamage = damage;
      if (directAttackTriggerContext.accumulatedDamageBonus > 0) {
        finalDamage += directAttackTriggerContext.accumulatedDamageBonus;
        addLog(`攻撃時効果でダメージ+${directAttackTriggerContext.accumulatedDamageBonus}！`, 'info');
      }

      if (currentPlayer === 1) {
        setP2Life(prev => Math.max(0, prev - finalDamage));

        // 飛行からの反撃で攻撃者が破壊される場合
        if (attackerDamaged && newAttackerHpAfterCounter <= 0) {
          // 破壊時トリガーを発火
          const destroyContext = {
            currentPlayer: 1,
            destroyedCard: attacker,
            destroyedSlotIndex: attackerIndex,
            setP1Life,
            setP2Life,
            setP1Field,
            setP2Field,
            setP1Hand,
            setP2Hand,
            setP1Deck,
            setP2Deck,
            setP1Graveyard,
            setP2Graveyard,
            setP1FieldCard,
            setP2FieldCard,
            p1Field,
            p2Field,
            p1Hand,
            p2Hand,
            p1Deck,
            p2Deck,
            p1Graveyard,
            p2Graveyard,
            p1FieldCard,
            p2FieldCard,
            p1Life,
            p2Life,
            addLog,
            registerCardTriggers,
            continuousEffectEngine,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireTrigger(TRIGGER_TYPES.ON_DESTROY_SELF, destroyContext as any);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireLeaveFieldTrigger(attacker, destroyContext as any, 'destroy');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handleLinkBreak(attacker, setP1Field as any, addLog);
          unregisterCardTriggers(attacker.uniqueId);
          continuousEffectEngine.unregister(attacker.uniqueId);

          setP1Field(prev => {
            const newField = [...prev];
            newField[attackerIndex] = null;
            return newField;
          });
          setP1Graveyard(prev => [...prev, attacker]);
          addLog(`${attacker.name}は飛行モンスターの反撃で破壊された！`, 'damage');
        } else {
          setP1Field(prev => {
            const newField = [...prev];
            const currentAttacker = newField[attackerIndex];
            if (currentAttacker) {
              const newHp = attackerDamaged ? newAttackerHpAfterCounter : currentAttacker.currentHp;
              newField[attackerIndex] = { ...currentAttacker, currentHp: newHp, canAttack: false };
            }
            return newField;
          });
        }
      } else {
        setP1Life(prev => Math.max(0, prev - finalDamage));

        // 飛行からの反撃で攻撃者が破壊される場合
        if (attackerDamaged && newAttackerHpAfterCounter <= 0) {
          // 破壊時トリガーを発火
          const destroyContext = {
            currentPlayer: 2,
            destroyedCard: attacker,
            destroyedSlotIndex: attackerIndex,
            setP1Life,
            setP2Life,
            setP1Field,
            setP2Field,
            setP1Hand,
            setP2Hand,
            setP1Deck,
            setP2Deck,
            setP1Graveyard,
            setP2Graveyard,
            setP1FieldCard,
            setP2FieldCard,
            p1Field,
            p2Field,
            p1Hand,
            p2Hand,
            p1Deck,
            p2Deck,
            p1Graveyard,
            p2Graveyard,
            p1FieldCard,
            p2FieldCard,
            p1Life,
            p2Life,
            addLog,
            registerCardTriggers,
            continuousEffectEngine,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireTrigger(TRIGGER_TYPES.ON_DESTROY_SELF, destroyContext as any);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fireLeaveFieldTrigger(attacker, destroyContext as any, 'destroy');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handleLinkBreak(attacker, setP2Field as any, addLog);
          unregisterCardTriggers(attacker.uniqueId);
          continuousEffectEngine.unregister(attacker.uniqueId);

          setP2Field(prev => {
            const newField = [...prev];
            newField[attackerIndex] = null;
            return newField;
          });
          setP2Graveyard(prev => [...prev, attacker]);
          addLog(`${attacker.name}は飛行モンスターの反撃で破壊された！`, 'damage');
        } else {
          setP2Field(prev => {
            const newField = [...prev];
            const currentAttacker = newField[attackerIndex];
            if (currentAttacker) {
              const newHp = attackerDamaged ? newAttackerHpAfterCounter : currentAttacker.currentHp;
              newField[attackerIndex] = { ...currentAttacker, currentHp: newHp, canAttack: false };
            }
            return newField;
          });
        }
      }

      // 【毒侵】判定: ダイレクトアタック成功時に相手プレイヤーを毒状態にする
      if (shouldApplyDokushin(attacker, damage)) {
        const setOpponentStatusEffects = currentPlayer === 1 ? setP2StatusEffects : setP1StatusEffects;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setOpponentStatusEffects(prev => [
          ...prev,
          { type: 'poison', value: 100, source: attacker.id, sourceName: attacker.name } as any
        ]);
        addLog(`【毒侵】${attacker.name}により相手プレイヤーが毒状態に！（毎ターン100ダメージ）`, 'damage');
      }

      // ON_DIRECT_ATTACK_SUCCESS トリガーを発火
      const directAttackSuccessContext = {
        currentPlayer,
        attacker,
        attackerIndex,
        card: attacker,
        slotIndex: attackerIndex,
        damage,
        p1Field,
        p2Field,
        p1Hand,
        p2Hand,
        p1Deck,
        p2Deck,
        p1Graveyard,
        p2Graveyard,
        p1Life,
        p2Life,
        setP1Field,
        setP2Field,
        setP1Hand,
        setP2Hand,
        setP1Deck,
        setP2Deck,
        setP1Graveyard,
        setP2Graveyard,
        setP1Life,
        setP2Life,
        addLog,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fireTrigger(TRIGGER_TYPES.ON_DIRECT_ATTACK_SUCCESS, directAttackSuccessContext as any);
    }

    setAttackingMonster(null);
    setSelectedFieldMonster(null);
  }, [isFirstTurn, currentPlayer, firstPlayer, p1Field, p2Field, p1FieldCard, p2FieldCard,
      p1Life, p2Life, p1Hand, p2Hand, p1Deck, p2Deck, p1Graveyard, p2Graveyard,
      p1StatusEffects, p2StatusEffects, loneWarriorRule, piercingDamage,
      setP1Life, setP2Life, setP1Field, setP2Field, setP1Hand, setP2Hand,
      setP1Deck, setP2Deck, setP1Graveyard, setP2Graveyard,
      setP1FieldCard, setP2FieldCard, setP1StatusEffects, setP2StatusEffects,
      setAttackingMonster, setSelectedFieldMonster, addLog]);

  // =============================================================================
  // 攻撃開始（チェーン確認を挟む公開API）
  // =============================================================================

  const attack = useCallback((attackerIndex: number, targetIndex: number | null): void => {
    // チェーン確認中の場合は無視
    if (chainConfirmation) {
      addLog('チェーン確認中です', 'info');
      return;
    }

    // 先攻1ターン目は攻撃不可
    if (isFirstTurn && currentPlayer === firstPlayer) {
      addLog('先攻1ターン目は攻撃できません', 'info');
      return;
    }

    // 基本的なバリデーション
    const playerField = currentPlayer === 1 ? p1Field : p2Field;
    const attacker = playerField[attackerIndex];
    if (!attacker || !attacker.canAttack) {
      addLog('このモンスターは攻撃できません', 'damage');
      return;
    }

    // 状態異常による攻撃制限をチェック（チェーン確認前に判定）
    if (!statusEffectEngine.canAttack(attacker)) {
      const statusName = statusEffectEngine.getBlockingStatusName(attacker, 'attack');
      addLog(`${attacker.name}は${statusName}により攻撃できません！`, 'damage');
      return;
    }

    // 攻撃宣言時のチェーンポイントを開始
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const needsConfirmation = startChainConfirmation(
      CHAIN_POINTS.ATTACK_DECLARATION,
      { type: 'attack', attackerIndex, targetIndex } as any,
      { attacker }
    );

    if (!needsConfirmation) {
      // チェーン確認不要（相手が刹那詠唱を持っていない）→ 直接攻撃実行
      executeAttack(attackerIndex, targetIndex);
    }
    // needsConfirmation === true の場合、確認UIが表示される
  }, [chainConfirmation, isFirstTurn, currentPlayer, firstPlayer, p1Field, p2Field,
      startChainConfirmation, executeAttack, addLog]);

  return {
    executeAttack,
    attack,
  };
};

export default useCombat;
